import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUILoadingLinear } from '../../../atomic/atoms';
import RpaQuery from '../../../services/query/rpa/RpaQuery.services';
import TimeHelper from '../../../utils/time';

export type RpaRequestSummary = {
    rpaRequestId: number;
    userName: string;
    createDate: string;
    statusKey: string;
};

type RpaActionRow = {
    id: number;
    shippingListNo: string;
    lastActionType: string;
    lastActionStatus: string;
    lastActionDate: string;
};

const extractList = (res: any): any[] => {
    if (!res) return [];
    return res.model?.data ?? res.data ?? res.model ?? [];
};

type Props = {
    request: RpaRequestSummary | null;
};

export const RpaRequestDetailDrawer: FC<Props> = ({ request }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [actions, setActions] = useState<RpaActionRow[]>([]);

    useEffect(() => {
        if (!request?.rpaRequestId) {
            setActions([]);
            return;
        }

        setLoading(true);
        RpaQuery.RpaActionList({ take: 500, skip: 0, rpaRequestId: request.rpaRequestId })
            .then((res: any) => {
                const rows = extractList(res).map((item: any) => ({
                    id: item.rpaRequestActionId ?? item.RPARequestActionId,
                    shippingListNo: item.shippingListNo ?? item.ShippingListNo ?? '',
                    lastActionType: item.lastActionType ?? item.LastActionType ?? '',
                    lastActionStatus: item.lastActionStatus ?? item.LastActionStatus ?? '',
                    lastActionDate: item.lastActionDate ?? item.LastActionDate ?? '',
                }));
                setActions(rows);
            })
            .catch((error: unknown) => {
                console.error('RpaActionList error:', error);
                setActions([]);
            })
            .finally(() => setLoading(false));
    }, [request?.rpaRequestId]);

    const columns = useMemo(
        () => [
            {
                field: 'shippingListNo',
                headerName: t('rpaRequestManagement.columns.shippingListNo'),
                flex: 1,
                minWidth: 160,
            },
            {
                field: 'lastActionType',
                headerName: t('rpaRequestManagement.columns.actionType'),
                flex: 1,
                minWidth: 180,
            },
            {
                field: 'lastActionStatus',
                headerName: t('rpaRequestManagement.columns.actionStatus'),
                flex: 1.2,
                minWidth: 220,
            },
            {
                field: 'lastActionDate',
                headerName: t('rpaRequestManagement.columns.actionDate'),
                flex: 1,
                minWidth: 160,
                valueGetter: (_: unknown, row: RpaActionRow) =>
                    row.lastActionDate ? TimeHelper.formatDateHour(row.lastActionDate) : '',
            },
        ],
        [t]
    );

    if (!request) {
        return null;
    }

    return (
        <Box className="rpa-request-detail-drawer" sx={{ p: 2, width: '100%' }}>
            <Box className="rpa-request-detail-drawer__header">
                <Typography className="rpa-request-detail-drawer__title" variant="h6" fontWeight={700}>
                    {t('rpaRequestManagement.detailTitle', { id: request.rpaRequestId })}
                </Typography>
                <Typography className="rpa-request-detail-drawer__meta" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('rpaRequestManagement.detailMeta', {
                        user: request.userName,
                        date: TimeHelper.formatDateHour(request.createDate),
                        status: request.statusKey,
                    })}
                </Typography>
            </Box>
            <Box className="rpa-request-detail-drawer__loading-wrap" sx={{ mt: 2 }}>
                <SCUILoadingLinear display={loading} />
            </Box>
            <Box className="rpa-request-detail-drawer__grid-wrap" sx={{ mt: 2, height: 480, width: '100%' }}>
                {!loading && actions.length === 0 ? (
                    <Typography className="rpa-request-detail-drawer__empty-wrap" color="text.secondary">
                        {t('general.noDataFound')}
                    </Typography>
                ) : (
                    <SCUICustomDataGrid randomId rows={actions} columns={columns} />
                )}
            </Box>
        </Box>
    );
};
